import { useEffect, useId, useRef, useState } from 'react';
import { MoreHorizontal } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface OverflowMenuItem {
  readonly key: string;
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  readonly title?: string;
  /**
   * Visible helper text under the item, also its accessible description
   * (`aria-describedby`): for example why a disabled item cannot be used,
   * which a `title` tooltip alone does not show on touch or reliably announce.
   */
  readonly description?: string;
}

export interface OverflowMenuProps {
  readonly label: string;
  readonly items: readonly OverflowMenuItem[];
}

/**
 * A small menu button. Arrow keys move, Enter or Space select, Escape closes
 * and gives focus back to the button, and a click elsewhere closes it.
 */
export function OverflowMenu({ label, items }: OverflowMenuProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const enabledItems = (): HTMLButtonElement[] =>
    Array.from(
      rootRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [],
    ).filter((element) => !element.disabled);

  useEffect(() => {
    if (!open) return undefined;
    enabledItems()[0]?.focus();
    const onPointerDown = (event: PointerEvent): void => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const close = (returnFocus: boolean): void => {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault();
      close(true);
      return;
    }
    if (event.key === 'Tab') {
      setOpen(false);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    if (!open) {
      setOpen(true);
      return;
    }
    const focusable = enabledItems();
    if (focusable.length === 0) return;
    const current = focusable.indexOf(document.activeElement as HTMLButtonElement);
    const step = event.key === 'ArrowDown' ? 1 : -1;
    const next = (current + step + focusable.length) % focusable.length;
    focusable[next]?.focus();
  };

  return (
    <div ref={rootRef} className="relative" onKeyDown={onKeyDown}>
      <Button
        ref={triggerRef}
        type="button"
        variant="outline"
        size="icon"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid="overflow-menu-trigger"
        onClick={() => setOpen((current) => !current)}
      >
        <MoreHorizontal aria-hidden="true" className="size-4" />
      </Button>
      {open ? (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          className="border-border bg-popover text-popover-foreground shadow-admin-elevated absolute end-0 top-full z-30 mt-1 min-w-48 rounded-lg border p-1"
        >
          {items.map((item) => {
            const descriptionId = item.description ? `${menuId}-${item.key}-hint` : undefined;
            return (
              <div key={item.key}>
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  title={item.title}
                  aria-describedby={descriptionId}
                  onClick={() => {
                    close(false);
                    item.onSelect();
                  }}
                  className="hover:bg-admin-surface-muted focus-visible:bg-admin-surface-muted block w-full rounded-md px-3 py-2 text-start text-sm outline-none disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {item.label}
                </button>
                {item.description ? (
                  <p
                    id={descriptionId}
                    className="text-muted-foreground max-w-64 px-3 pb-2 text-start text-xs"
                  >
                    {item.description}
                  </p>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
